import BreadCrumbV2 from "../../../components/BreadCrumbV2";
import EditorJS from "@editorjs/editorjs";
import Header from "@editorjs/header";
import Underline from "@editorjs/underline";
import { useEffect, useRef, useState } from "react";
import { TbTemplate } from "react-icons/tb";
import { FaChartLine } from "react-icons/fa6";
import { api } from "../../../api";
import templateImage from "../assets/documentTemplate.png";
import { DocumentTemplate } from "../../../types";
import { MdChevronRight, MdChevronLeft } from "react-icons/md";
import { NotificationModal } from "../../../components/NotificationModal";
import { useSearchParams } from "react-router-dom";
import _ from "lodash";
import { useToast } from "@chakra-ui/react";
import { Switch } from "@chakra-ui/react";
import ImageTool from "@editorjs/image";

// notification modal state type
type NotificationState = {
  isSuccess: boolean;
  isOpenModal: boolean;
  title: string;
  content: string;
};

type PddEditorState = {
  isUpdate: boolean;
  hasSelectedTemplate: boolean;
  hasChangedTemplate: boolean;
  initialVersion: any | undefined;
  lastSavedVersion: any | undefined;
};

const AddNewPDD = () => {
  const toast = useToast();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const [searchParams] = useSearchParams();
  const pddID: any = searchParams?.get("pddID");

  const [editor, setEditor] = useState<EditorJS | null>(null); // move to pdd editor
  const [templateSectionExpanded, setTemplateSectionExpanded] = useState<boolean>(false);
  const [elementSectionExpanded, setElementSectionExpanded] = useState<boolean>(false);
  const [documentTemplates, setDocumentTemplates] = useState<DocumentTemplate[]>([]);
  const [scroll, setScroll] = useState({
    canScrollLeft: false,
    canScrollRight: false,
  });
  const [initializedPDD, setInitializedPDD] = useState<number | undefined>(undefined); // move to pdd editor state
  // notification modal state
  const [notificatioModalState, setNotificationModalState] = useState<NotificationState>({
    title: "",
    content: "",
    isOpenModal: false,
    isSuccess: false,
  });
  // const [savedPddData, setSavedPddData] = useState<SavedPddType | undefined>(undefined); // move to pdd editor state
  const [autoSave, setAutoSave] = useState<boolean>(false);
  const [pddEditorState, setPddEditorState] = useState<PddEditorState>({
    isUpdate: false,
    hasSelectedTemplate: false,
    hasChangedTemplate: false,
    lastSavedVersion: undefined,
    initialVersion: undefined,
  });
  const [recommendationLocations, setRecommendationLocations] = useState<string[]>([]);
  const [selectedElements, setSelectedElements] = useState<any[]>([]);

  // editor ready state
  const isReady = useRef(false);
  const scrollContainerRef = useRef(null);

  // If has PDDID params get the relevant docuemnt
  useEffect(() => {
    // call /documents/{document_id} endpoint to get the saved content
    if (pddID != null) {
      const getSavedPDD = async () => {
        const savedPDDResponse = await api.get(`/documents/${pddID}`);
        setPddEditorState((prev) => {
          return {
            ...prev,
            isUpdate: true,
            lastSavedVersion: savedPDDResponse?.data,
            initialVersion: savedPDDResponse?.data,
          };
        });
      };
      getSavedPDD();
    }
  }, [pddID]);

  // call only in update state
  useEffect(() => {
    if (pddID !== null && pddEditorState?.lastSavedVersion && editor?.isReady) {
      const renderContent = async () => {
        try {
          // Clear existing content first
          await editor.blocks.clear();
          // Then render the new content
          await editor.blocks.render(pddEditorState.lastSavedVersion);
          setInitializedPDD(pddID);
        } catch (error) {
          console.error("Error rendering content:", error);
          toast({
            title: "Error loading document",
            description: "There was an error loading the document content.",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        }
      };

      renderContent();
    }
  }, [pddEditorState?.lastSavedVersion, editor?.isReady, pddID]);

  useEffect(() => {
    const checkScrollability = () => {
      if (scrollContainerRef.current) {
        const container = scrollContainerRef.current as HTMLDivElement;

        // Check if total content width exceeds container width
        const canScroll = container.scrollWidth > container.clientWidth;

        setScroll({
          canScrollLeft: container.scrollLeft > 0,
          canScrollRight:
            canScroll && container.scrollLeft + container.clientWidth < container.scrollWidth,
        });
      }
    };

    // Check scrollability initially
    checkScrollability();

    // Add resize observer to handle dynamic content changes
    const container = scrollContainerRef.current;
    const resizeObserver = new ResizeObserver(checkScrollability);

    if (container) {
      resizeObserver.observe(container);
    }

    // Clean up the observer
    return () => {
      if (container) {
        resizeObserver.unobserve(container);
      }
    };
  }, [documentTemplates]);

  useEffect(() => {
    if (!isReady.current) {
      const editor = new EditorJS({
        holder: "editorjs",
        autofocus: true,
        placeholder: "write something...",
        inlineToolbar: true,
        tools: {
          header: {
            // @ts-expect-error header config is giving errors
            class: Header,
            config: {
              placeholder: "Enter a header",
              levels: [1, 2, 3, 4, 5, 6],
              defaultLevel: 1,
            },
          },
          underline: Underline,
          image: {
            class: ImageTool,
            config: {
              // Disable default drag and drop handling
              // disableDefaultDropzone: true,
              uploader: {
                uploadByFile(file: any) {
                  return Promise.resolve({
                    success: 1,
                    file: {
                      url: URL.createObjectURL(file),
                    },
                  });
                },
                uploadByUrl(url: any) {
                  return Promise.resolve({
                    success: 1,
                    file: {
                      url: url,
                    },
                  });
                },
              },
            },
          },
        },
      });
      setEditor(editor);
      isReady.current = true;
    }
    fetchDocumentTemplates().then((response) => {
      const documentTemplates: DocumentTemplate[] = response?.data?.blobs;
      setDocumentTemplates(documentTemplates);
    });

    fetchRecommendationProjects().then((response) => {
      if (response) {
        setRecommendationLocations(response?.data?.items);
      }
    });
  }, []);

  // function to set the template to editor and initialized a new PDD
  const handleSelectTemplate = async (selectedTemplate: DocumentTemplate) => {
    if (selectedTemplate?.name?.endsWith(".json") && editor?.isReady) {
      await editor?.blocks?.render(JSON.parse(selectedTemplate?.content));

      // add new pdd initial state
      setPddEditorState((prev) => {
        return {
          ...prev,
          initialVersion: JSON.parse(selectedTemplate?.content),
        };
      });

      // init-pdd body
      // TODO: fixed values should be removed later
      const initPDDBody = {
        name: `testing-pdd-${new Date()}`, // for testing generate an unique file name by combining the date and time
        description: "This is an example description.",
        project_id: "project_12345",
        pdd_template_id: selectedTemplate?.name,
      };

      // call init-pdd document
      const responseInitPDD = await api.post(`/init-pdd`, initPDDBody);
      setInitializedPDD(responseInitPDD?.data?.id);
    }
  };

  // function to handle is Open Modal
  const handleOpenModal = () => {
    setNotificationModalState((prev) => {
      return {
        ...prev,
        isOpenModal: !notificatioModalState?.isOpenModal,
      };
    });
  };

  // function to scroll between document template
  // TODO: Set an appropriate type for direction
  const handleScroll = (direction: any) => {
    if (scrollContainerRef.current) {
      const container: any = scrollContainerRef.current;
      const itemWidth = container.children[0].getBoundingClientRect().width;

      // Scroll one item width in the specified direction
      container.scrollBy({
        left: direction === "left" ? -itemWidth : itemWidth,
        behavior: "smooth",
      });

      setTimeout(() => {
        setScroll({
          canScrollLeft: container.scrollLeft > 0,
          canScrollRight: container.scrollLeft + container.clientWidth < container.scrollWidth,
        });
      }, 300);
    }
  };

  // fectch document templates
  const fetchDocumentTemplates = async () => {
    // TODO: Get templates by page number
    const response = await api.get(`/document-templates/?per_page=10&page=1`);
    return response;
  };

  // fetch recommendation Projects
  const fetchRecommendationProjects = async () => {
    const recommendationProjects = await api.get(
      `/v2/recommendations-by-districts/?id=ALL&level=0&search=&skip=0&limit=68`,
    );
    return recommendationProjects;
  };

  const saveDataFunction = async (isAutoSaveAction: boolean) => {
    // two states to handle - update existing pdd, add new pdd
    // add new pdd
    if (initializedPDD) {
      try {
        const pddData = await editor?.save();
        // call compare content function to compare the current content with the initial state
        const isContentEqual = await compareContent();
        if (!isContentEqual) {
          // content to update
          const content = {
            content: pddData,
          };
          // call the update doc endpoint
          const updateDocResponse = await api.put(`/documents/${initializedPDD}`, content);
          if (updateDocResponse?.status === 200) {
            if (isAutoSaveAction) {
              toast({
                title: "Current content saved successfully",
                status: "success",
                duration: 3000,
                isClosable: true,
              });
            } else {
              setNotificationModalState(() => {
                return {
                  title: "Document saved Successfully!",
                  content: `${updateDocResponse?.data?.name} document saved Successfully`,
                  isOpenModal: true,
                  isSuccess: true,
                };
              });
            }
            // Set last saved version
            setPddEditorState((prev) => {
              return {
                ...prev,
                lastSavedVersion: pddData,
              };
            });
          } else {
            if (isAutoSaveAction) {
              toast({
                title: "An Unexpect Error Occured while saving",
                status: "error",
                duration: 3000,
                isClosable: true,
              });
            } else {
              setNotificationModalState(() => {
                return {
                  title: "Error Occured!",
                  content: `An Unexpect Error Occured ${updateDocResponse?.status}`,
                  isOpenModal: true,
                  isSuccess: false,
                };
              });
            }
          }
        } else {
          setNotificationModalState(() => {
            return {
              title: "The Document has not changed yet!",
              content:
                "The template cannot be saved because no changes have been made since the last save.",
              isOpenModal: true,
              isSuccess: false,
            };
          });
        }
      } catch (err) {
        setNotificationModalState(() => {
          return {
            title: "Error Occured!",
            content: `An Unexpect Error Occured ${err}`,
            isOpenModal: true,
            isSuccess: false,
          };
        });
      }
    }

    // if not initialized
    if (initializedPDD === undefined) {
      setNotificationModalState(() => {
        return {
          title: "A Template Not Selected",
          content: `A template must be selected to create a new Project Design Document.`,
          isOpenModal: true,
          isSuccess: false,
        };
      });
    }
    // update state
  };

  const compareContent = async () => {
    if (pddEditorState?.initialVersion) {
      const currentContent = await editor?.save();
      // remove time property to compare two contents
      const currentContentWithoutTime = _.omit(currentContent, ["time", "version"]);
      const initialContentWithoutTime = _.omit(pddEditorState?.initialVersion, ["time", "version"]);

      if (!pddEditorState?.lastSavedVersion) {
        return _.isEqual(currentContentWithoutTime, initialContentWithoutTime);
      } else {
        const lastSavedVersionWihtoutTime = _.omit(pddEditorState?.lastSavedVersion, [
          "time",
          "version",
        ]);
        return _.isEqual(currentContentWithoutTime, lastSavedVersionWihtoutTime);
      }
    }
  };

  useEffect(() => {
    if (autoSave) {
      const runAutoSave = async () => {
        const isContentEqual = await compareContent();
        if (!isContentEqual) {
          // Save the data
          await saveDataFunction(true);
          // Re-run the autosave logic after the interval
          intervalRef.current = setTimeout(runAutoSave, 30000); // 30 seconds
        } else {
          // Optionally, recheck content after the interva
          intervalRef.current = setTimeout(runAutoSave, 30000); // 30 seconds
        }
      };
      // Start the autosave process
      runAutoSave();
    }

    // Cleanup when autoSave is disabled or component unmounts
    return () => {
      if (intervalRef.current) {
        clearTimeout(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [autoSave, compareContent, saveDataFunction]);

  // function to handle export as PDF
  // const handleExportDocumentPDF = async () => {
  //   try {
  //     if (!pddEditorState?.isUpdate && initializedPDD) {
  //       // compare the content
  //       const isContentEqual = await compareContent();
  //       if (!isContentEqual) {
  //         await handleDownloadPDF(initializedPDD);
  //       } else {
  //         setNotificationModalState(() => {
  //           return {
  //             title: "Template Not Updated",
  //             content:
  //               "The template cannot be saved because no changes have been made. Please update the template before attempting to download.",
  //             isOpenModal: true,
  //             isSuccess: false,
  //           };
  //         });
  //       }
  //     }
  //   } catch (error) {
  //     setNotificationModalState(() => {
  //       return {
  //         title: "An unexpected error occured",
  //         content: `An error occured ${error}`,
  //         isOpenModal: true,
  //         isSuccess: false,
  //       };
  //     });
  //   }
  // };

  // const handleDownloadPDF = async (pddID: number) => {
  //   const downloadResponse = await api.get(`/documents/export/${pddID}`, {
  //     responseType: "blob",
  //   });

  //   const pdfBlob = new Blob([downloadResponse?.data], { type: "application/pdf" });

  //   // create a temp url for the Blob
  //   const url = window.URL.createObjectURL(pdfBlob);

  //   const tempLink = document.createElement("a");
  //   tempLink.href = url;
  //   tempLink.setAttribute("download", `pdd_${new Date()}.pdf`);

  //   document.body.appendChild(tempLink);
  //   tempLink.click();

  //   // clean up the temporary elements and URL
  //   document.body.removeChild(tempLink);
  //   window.URL.revokeObjectURL(url);
  // };

  // handle autoSave enable disable functionality
  const handleAutoSave = () => {
    if (initializedPDD) {
      setAutoSave(!autoSave);
    } else {
      toast({
        title: "A PDD has not initialized yet.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleCancel = () => {
    if (editor?.isReady) {
      editor?.blocks?.render(pddEditorState?.lastSavedVersion || pddEditorState?.initialVersion);
    }
  };

  // function to expand sections
  const expandSections = (sectionType: string) => {
    if (sectionType === "template") {
      // set element exapand to flase prevent opening the both sections
      setElementSectionExpanded(false);
      setTemplateSectionExpanded(!templateSectionExpanded);
    } else {
      if (initializedPDD === undefined) {
        toast({
          title: "You cannot get elements",
          description: "A template must be selected to get elements.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        return;
      }
      setTemplateSectionExpanded(false);
      setElementSectionExpanded(!elementSectionExpanded);
    }
  };

  const handleGetElements = async (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = JSON.parse(event.target.value); // Get the selected value
    // get elements
    const elementsResponse = await api.get(
      `/documents/elements/?id=${selectedValue?.country_code}&adm_name=${selectedValue?.adm2}`,
    );
    if (elementsResponse) {
      setSelectedElements(elementsResponse?.data?.elements);
    }
  };

  // drag and drop functionalit
  const handleDragStart = (e: React.DragEvent, imageUrl: string) => {
    // Set the drag data with the image URL
    e.dataTransfer.setData("text/plain", imageUrl);
    e.dataTransfer.effectAllowed = "copy";
  };

  const renderElements = () => (
    <div
      className="flex space-x-4 overflow-x-auto scroll-smooth pb-2 scrollbar-hide"
      ref={scrollContainerRef}
    >
      {selectedElements?.map((element: string) => (
        <div
          key={element}
          className="flex flex-shrink-0 cursor-move flex-col items-center"
          draggable="true"
          onDragStart={(e) => handleDragStart(e, element)}
        >
          <img src={element} alt="elementImage" className="h-24 w-24 rounded-md object-cover" />
          <p className="mt-1 w-24 truncate text-center text-xs">
            {element.split("/").pop()?.split(".")[0]}
          </p>
        </div>
      ))}
    </div>
  );

  return (
    <div className="flex flex-col" style={{ height: `calc(100vh - 76px)` }}>
      {/* Header section */}
      <div className="flex items-center justify-between px-4">
        <BreadCrumbV2 />
        {/* switch autosave */}
        <div>
          <label htmlFor="email-alerts" className="mr-2">
            Enable Auto Save
          </label>
          <Switch id="autosave" onChange={handleAutoSave} isChecked={autoSave} />
        </div>
      </div>

      {/* Main content area */}
      <div className="flex-grow px-4">
        {/* main box */}
        <div className="my-4 flex">
          {/* sidebar */}
          <div className="w-1/12 space-y-8 rounded-l-md bg-white shadow-md">
            <div
              className={`flex cursor-pointer flex-col items-center ${
                templateSectionExpanded ? "bg-[#482F8B33]" : "bg-none"
              } p-4 text-primaryPurple-200`}
              onClick={() => expandSections("template")}
            >
              <TbTemplate />
              <span>Template</span>
            </div>
            <div
              className={`flex cursor-pointer flex-col items-center ${
                elementSectionExpanded ? "bg-[#482F8B33]" : "bg-none"
              } p-4 text-primaryPurple-200`}
              onClick={() => expandSections("element")}
            >
              <FaChartLine />
              <span>Elements</span>
            </div>
          </div>

          {/* Expandable section - templates */}
          <div
            className={`w-3/12 ${
              templateSectionExpanded ? "block" : "hidden"
            } rounded-r-md bg-[#482F8B0D] px-4 py-2`}
          >
            {/* all methodology templates */}
            <div className="my-2">
              <h3 className="mb-2 font-medium text-black">All Methodology Templates</h3>

              <div className="relative my-2 flex items-center">
                {scroll?.canScrollLeft && (
                  <button
                    onClick={() => handleScroll("left")}
                    className="absolute left-0 top-9 z-10 p-1"
                  >
                    <MdChevronLeft
                      className="rounded-md border border-[#231F201A] bg-white text-xl text-primaryPurple-200 shadow-md"
                      size={25}
                    />
                  </button>
                )}

                {/* start - template map */}
                <div
                  className="flex space-x-4 overflow-x-auto scroll-smooth pb-2 scrollbar-hide"
                  ref={scrollContainerRef}
                >
                  {documentTemplates?.map((template: DocumentTemplate) => (
                    <div
                      key={template?.name}
                      className="flex flex-shrink-0 cursor-pointer flex-col items-center"
                      onClick={() => handleSelectTemplate(template)}
                    >
                      <img
                        src={templateImage}
                        alt="templateImage"
                        className="h-24 w-24 rounded-md object-cover"
                      />
                      <p className="mt-1 w-24 truncate text-center text-xs">
                        {template?.name?.replace(".json", "")}
                      </p>
                    </div>
                  ))}
                </div>
                {/* end - template map */}

                {scroll?.canScrollRight && (
                  <button
                    onClick={() => handleScroll("right")}
                    className="absolute right-0 top-9 z-10 p-1"
                  >
                    <MdChevronRight
                      className="rounded-md border border-[#231F201A] bg-white text-xl text-primaryPurple-200 shadow-md"
                      size={25}
                    />
                  </button>
                )}
              </div>
            </div>
          </div>
          {/* End - Expandable section - templates */}

          {/* Expandable section - elements */}
          <div
            className={`w-3/12 ${
              elementSectionExpanded ? "block" : "hidden"
            } rounded-r-md bg-[#482F8B0D] px-4 py-2`}
          >
            <div className="flex items-center justify-between gap-2">
              <select
                name="methodologyType"
                id="methodologyType"
                className="w-full rounded-md border border-[#231F201A] px-2 py-2 outline-none"
                defaultValue="allMethodologyType"
                onChange={handleGetElements}
              >
                <option value="allMethodologyType" disabled>
                  Select a location
                </option>
                {recommendationLocations?.map((location: any, index: number) => {
                  return (
                    <option value={JSON.stringify(location)} key={index}>
                      {location?.adm2}
                    </option>
                  );
                })}
              </select>
              {/* <input
                type="text"
                name="methodologyTypeTextInput"
                id="methodologyTypeTextInput"
                className="w-full rounded-md border border-[#231F201A] px-2 py-2 outline-none"
              /> */}
            </div>

            {/* all methodology templates */}
            <div className="my-2">
              <h3 className="mb-2 font-medium text-black">Elements</h3>

              <div className="relative my-2 flex items-center">
                {scroll?.canScrollLeft && (
                  <button
                    onClick={() => handleScroll("left")}
                    className="absolute left-0 top-9 z-10 p-1"
                  >
                    <MdChevronLeft
                      className="rounded-md border border-[#231F201A] bg-white text-xl text-primaryPurple-200 shadow-md"
                      size={25}
                    />
                  </button>
                )}

                {renderElements()}
                {/* start - template map */}
                {/* <div
                  className="flex space-x-4 overflow-x-auto scroll-smooth pb-2 scrollbar-hide"
                  ref={scrollContainerRef}
                >
                  {selectedElements?.map((element: any) => (
                    <div
                      key={element}
                      className="flex flex-shrink-0 cursor-pointer flex-col items-center"
                      // onClick={() => handleSelectTemplate(template)}
                    >
                      <img
                        src={element}
                        alt="elementImage"
                        className="h-24 w-24 rounded-md object-cover"
                      />
                      <p className="mt-1 w-24 truncate text-center text-xs">
                        {element.split("/").pop()?.split(".")[0]}
                      </p>
                    </div>
                  ))}
                </div> */}
                {/* end - template map */}

                {scroll?.canScrollRight && (
                  <button
                    onClick={() => handleScroll("right")}
                    className="absolute right-0 top-9 z-10 p-1"
                  >
                    <MdChevronRight
                      className="rounded-md border border-[#231F201A] bg-white text-xl text-primaryPurple-200 shadow-md"
                      size={25}
                    />
                  </button>
                )}
              </div>
            </div>
          </div>
          {/* End - Expandable section - elemets */}

          {/* text editor */}
          <div
            className={`${
              templateSectionExpanded || elementSectionExpanded ? "w-8/12" : "w-11/12"
            } ml-2`}
          >
            <div
              id="editorjs"
              className={`rounded-md bg-white py-3 shadow-md ${
                templateSectionExpanded || elementSectionExpanded ? "pl-10" : ""
              } overflow-auto`}
              style={{ height: `calc(100vh - 250px)` }}
              // onDragOver={handleDragOver}
              // onDrop={handleDrop}
            ></div>
          </div>
        </div>
      </div>

      {/* Footer button section */}
      <div className="mt-auto">
        <div className="bg-white p-4 shadow-md">
          <div className="container mx-auto flex justify-end gap-4">
            <button
              type="submit"
              className="px-4 py-2 text-primaryPurple-100 hover:text-primaryPurple-200"
              onClick={handleCancel}
            >
              Cancel
            </button>
            {/* <button
              type="button"
              className={`rounded-full border border-primaryPurple-100 bg-white px-4 py-2 text-primaryPurple-100 duration-150 hover:bg-primaryPurple-100 hover:text-white`}
              onClick={handleExportDocumentPDF}
            >
              Export Document as PDF
            </button> */}
            <button
              type="submit"
              onClick={() => saveDataFunction(false)}
              className="rounded-full border border-primaryPurple-100 bg-primaryPurple-100 px-4 py-2 text-white hover:bg-white hover:text-primaryPurple-100"
            >
              Save
            </button>
            {/* testing compare content button */}
            {/*<button
              type="submit"
              onClick={compareContent}
              className="rounded-full border border-primaryPurple-100 bg-primaryPurple-100 px-4 py-2 text-white hover:bg-white hover:text-primaryPurple-100"
            >
              Testing Save
            </button> */}
          </div>
        </div>
      </div>
      <div className="bg-blue-500">
        <NotificationModal
          isOpen={notificatioModalState?.isOpenModal}
          handleOpen={handleOpenModal}
          title={notificatioModalState?.title}
          content={notificatioModalState?.content}
          isSuccess={notificatioModalState?.isSuccess}
        />
      </div>
    </div>
  );
};

export default AddNewPDD;
